import axios, {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	AxiosError,
} from "axios";
import { useAuthStore } from "@/stores/auth";
import i18n from "@/i18n";
import { showError, showWarning } from "@/services/notificationService";
import { promptUpgrade } from "@/composables/useUpgradePrompt";
import { getClientId } from "@/utils/clientId";

/**
 * Extrai a mensagem que o backend mandou no corpo do erro — **só quando ela foi
 * escrita para ser lida por gente**.
 *
 * O NestJS usa dois formatos no mesmo campo:
 *
 * - `{ message: "Médium não encontrado" }` — string, vinda de uma exceção de
 *   negócio que alguém escreveu em PT-BR pensando no usuário. Essa serve.
 * - `{ message: ["code must be a string", ...] }` — array, gerado pelo
 *   ValidationPipe. Texto em inglês citando o nome interno do campo, sem
 *   tradução possível aqui. Essa NÃO serve: mostrar isso num toast entrega
 *   detalhe de implementação e quebra a regra de PT-BR do projeto.
 *
 * Devolve `null` nos casos que não servem, para quem chama cair no texto padrão
 * da própria tela — genérico, mas em português e sob nosso controle.
 */
export function mensagemDoBackend(error: AxiosError): string | null {
	const dados = error.response?.data as
		| { message?: string | string[] }
		| undefined;
	const mensagem = dados?.message;

	return typeof mensagem === "string" && mensagem.trim() ? mensagem : null;
}

/**
 * Classe ApiService para gerenciar todas as requisições HTTP
 * Implementa um padrão Singleton para garantir uma única instância
 */
class ApiService {
	private static instance: ApiService;
	private api: AxiosInstance;
	private refreshPromise: Promise<string | null> | null = null;

	private constructor() {
		// Criar instância do Axios com configurações padrão
		this.api = axios.create({
			baseURL: `${import.meta.env.VITE_BACKEND_URL || ""}/api`,
			headers: {
				"Content-Type": "application/json",
			},
			timeout: 30000, // 30 segundos de timeout
		});

		// Configurar interceptors
		this.setupInterceptors();
	}

	/**
	 * Obtém a instância única do ApiService (Singleton)
	 */
	public static getInstance(): ApiService {
		if (!ApiService.instance) {
			ApiService.instance = new ApiService();
		}
		return ApiService.instance;
	}

	/**
	 * Configura os interceptors para requisições e respostas
	 */
	private setupInterceptors(): void {
		// Interceptor de requisição - adiciona token de autenticação
		this.api.interceptors.request.use(
			(config) => {
				const token = localStorage.getItem("token");
				if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				}

				// Identifica a ABA: o aviso de cadastro alterado volta para o
				// terreiro todo, e com isto quem fez a alteração ignora o próprio
				// eco em vez de recarregar a lista duas vezes (FEAT-044).
				config.headers["X-Client-Id"] = getClientId();

				// Adicionar CSRF token se disponível
				const csrfToken = localStorage.getItem("csrfToken");
				if (csrfToken) {
					config.headers["X-CSRF-Token"] = csrfToken;
				}

				return config;
			},
			(error) => Promise.reject(error)
		);

		// Interceptor de resposta - trata erros e refresh token
		this.api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;

				// Se erro 403 com SUBSCRIPTION_BLOCKED, redirecionar
				if (error.response?.status === 403 && error.response?.data?.error === 'SUBSCRIPTION_BLOCKED') {
					window.location.href = '/subscription-blocked';
					return Promise.reject(error);
				}

				// Limite/módulo do plano (409 corrente, 403 módulo) → abre o dialog
				// global de upgrade com a mensagem do backend. Marca o erro para o
				// chamador não exibir também um toast genérico (evita duplicidade).
				const planError = error.response?.data?.error;
				if (
					(error.response?.status === 409 &&
						(planError === 'CORRENTE_LIMIT_REACHED' ||
							planError === 'ADMIN_USERS_LIMIT_REACHED')) ||
					(error.response?.status === 403 && planError === 'MODULE_NOT_IN_PLAN')
				) {
					promptUpgrade(error.response?.data?.message);
					error.__planLimitHandled = true;
					return Promise.reject(error);
				}

				// Se erro 401 (Não autorizado) e ainda não tentamos refresh
				if (error.response?.status === 401 && !originalRequest._retry) {
					originalRequest._retry = true;

					// Mutex: se já tem um refresh em andamento, espera ele resolver
					if (!this.refreshPromise) {
						this.refreshPromise = this.refreshToken().finally(() => {
							this.refreshPromise = null;
						});
					}

					const newToken = await this.refreshPromise;

					if (newToken) {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
						return this.api(originalRequest);
					}
				}

				return Promise.reject(error);
			}
		);
	}

	/**
	 * Atualiza o token de acesso usando o refresh token
	 */
	private async refreshToken(): Promise<string | null> {
		try {
			const refreshTokenValue = localStorage.getItem("refreshToken");

			if (!refreshTokenValue) {
				console.error("Refresh token não encontrado no localStorage");
				return null;
			}

			// Usar axios diretamente para evitar loop infinito
			const response = await axios.post(
				`${import.meta.env.VITE_BACKEND_URL || ""}/api/auth/refresh-token`,
				{ refreshToken: refreshTokenValue }
			);

			// Verificar a estrutura da resposta
			let newToken = null;

			if (response.data.access_token) {
				newToken = response.data.access_token;
				localStorage.setItem("token", newToken);

				if (response.data.refresh_token) {
					localStorage.setItem("refreshToken", response.data.refresh_token);
				}
			} else {
				console.error("Formato de resposta desconhecido:", response.data);
				return null;
			}

			return newToken;
		} catch (error: any) {
			console.error("Erro ao renovar token:", error?.response?.data || error);
			// Só encerra a sessão em falha de autenticação real (refresh token inválido/
			// expirado). Erros de rede/servidor (timeout, 5xx durante deploy/restart do
			// PM2) são transitórios e NÃO devem descartar uma sessão ainda válida: não
			// limpamos tokens nem redirecionamos — o próximo request, ou o refresh
			// proativo do sessionManager, tenta de novo. Mesmo critério do auth store.
			const status = error?.response?.status;
			if (status === 401 || status === 403) {
				localStorage.removeItem("token");
				localStorage.removeItem("refreshToken");
				localStorage.removeItem("csrfToken");
				// Hard redirect: recarrega a app e descarta qualquer dado em tela.
				window.location.href = "/login";
			}
			return null;
		}
	}

	/**
	 * Métodos públicos para fazer requisições HTTP
	 */
	public async get<T = any>(
		url: string,
		config?: AxiosRequestConfig
	): Promise<T> {
		try {
			const response = await this.api.get<T>(url, config);
			return response.data;
		} catch (error) {
			this.handleApiError(error as AxiosError);
			throw error;
		}
	}

	public async post<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<T> {
		try {
			const response = await this.api.post<T>(url, data, config);
			return response.data;
		} catch (error) {
			this.handleApiError(error as AxiosError);
			throw error;
		}
	}

	public async put<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<T> {
		try {
			const response = await this.api.put<T>(url, data, config);
			return response.data;
		} catch (error) {
			this.handleApiError(error as AxiosError);
			throw error;
		}
	}

	public async delete<T = any>(
		url: string,
		config?: AxiosRequestConfig
	): Promise<T> {
		try {
			const response = await this.api.delete<T>(url, config);
			return response.data;
		} catch (error) {
			this.handleApiError(error as AxiosError);
			throw error;
		}
	}

	public async patch<T = any>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<T> {
		try {
			const response = await this.api.patch<T>(url, data, config);
			return response.data;
		} catch (error) {
			this.handleApiError(error as AxiosError);
			throw error;
		}
	}

	/**
	 * Método para tratar erros de API de forma centralizada
	 * Este método pode ser chamado de qualquer lugar onde se usa o apiService
	 */
	public handleApiError(error: AxiosError): void {
		// Usa o i18n diretamente ao invés do composable useI18n
		const t = (key: string) => i18n.global.t(key);

		// As telas mostram `err.message` no toast de erro. Sem isto o usuário lê
		// "Request failed with status code 400" — o Axios preenche `message` com a
		// descrição do status HTTP, não com o que o backend explicou. Trocando aqui,
		// UMA vez, toda tela que já faz `showError(err.message)` passa a mostrar o
		// motivo real ("Médium não encontrado") sem precisar de mudança própria.
		const doBackend = mensagemDoBackend(error);
		if (doBackend) {
			error.message = doBackend;
		}

		const noConnection = [
			"ERR_CONNECTION_REFUSED",
			"ERR_NETWORK",
			"ECONNREFUSED",
			"ENETUNREACH",
		].includes(error.code || "");

		// Sempre logar o erro para debugging
		console.error(
			"[ApiService] Error:",
			error.message,
			error.code,
			error.response?.status,
			error.response?.data
		);

		if (!error.response || noConnection) {
			// Erro de conexão (sem resposta do servidor)
			showError(t("api.errors.connection"), {
				timeout: 10000,
				actions: [
					{
						text: t("api.errors.retry"),
						action: () => {
							// reconnect handled by retry logic
						},
					},
				],
			});
		} else if (error.response?.status >= 500) {
			// Erro do servidor
			showError(t("api.errors.server"));
		} else if (error.response?.status === 429) {
			// Muitas requisições
			showWarning(t("api.errors.tooManyRequests"));
		} else if (
			error.response?.status === 408 ||
			error.code === "ECONNABORTED"
		) {
			// Timeout da requisição
			showError(t("api.errors.timeout"));
		}
		// Erros 4xx (400, 401, 403, 404, 409, 422...) não mostram toast genérico.
		// O backend já retorna uma mensagem descritiva (error.response.data.message)
		// e quem chamou a API é responsável por tratá-la e exibi-la ao usuário.
	}

	/**
	 * Obtém a instância do Axios para uso direto (se necessário)
	 */
	public getAxiosInstance(): AxiosInstance {
		return this.api;
	}
}

// Exportar uma instância única do ApiService
const apiService = ApiService.getInstance();
export default apiService;
